// src/services/chatApi.ts
import apiReq from 'core/api';
import { normalizeBackendUtcIsoString } from 'utils/dateUtils';
import type { ChatListing, ChatMsg, ContextFile, ContextFileSelection } from 'types';
import { applyCompanyPolicies, stripHiddenTails } from 'services/demoApi/promptDecorators';

// ─── Request / Response types ─────────────────────────────────────────────────

export interface SendMessageBody {
  human_message: string;
  /** Новый формат бэкенда: {id, headers?} вместо string[] */
  use_context_files?: ContextFileSelection[];
  context_messages_limit?: number | null;
  current_task_id?: string | null;
  /** Режим «Глубокий анализ документов»: в чате агента всегда false */
  analysys_mode?: boolean;
  [key: string]: unknown;
}

export interface SendMessageResponse {
  task_id: string;
}

// ─── Backend raw message shape ────────────────────────────────────────────────

interface BackendMessage {
  role: string;
  content: string;
  created_at?: string;
}

interface ChatsListResponse {
  count: number;
  items: ChatListing[];
}

/** Полное представление чата (GET /chat/{id}/view) — включает реальный состав
 * контекстных документов именно этого чата с флагом is_active */
export interface ChatView {
  id: string;
  title: string | null;
  scenario_id: string | null;
  project_id: string | null;
  user_id: string;
  context_documents: ContextFile[];
  created_at: number;
  updated_at: number;
}

// ─── API functions ────────────────────────────────────────────────────────────

/** Получить список чатов, опционально фильтруя по scenario_id */
export const listChats = async (scenarioId?: string): Promise<ChatListing[]> => {
  const query = scenarioId ? `?scenario_id=${encodeURIComponent(scenarioId)}` : '';
  const data = await apiReq<ChatsListResponse>(`chats${query}`);
  return data?.items ?? [];
};

/** Создать новый чат */
export const createChat = async (
  projectId: string,
  scenarioId?: string,
  title?: string
): Promise<ChatListing | undefined> => {
  return apiReq<ChatListing>('chats/create', 'POST', {
    project_id: projectId,
    ...(scenarioId !== undefined ? { scenario_id: scenarioId } : {}),
    ...(title !== undefined ? { title } : {}),
  });
};

/** Получить полное представление чата (метаданные + контекстные документы) */
export const getChatView = async (chatId: string): Promise<ChatView | undefined> => {
  return apiReq<ChatView>(`chat/${chatId}/view`);
};

/** Получить историю сообщений чата */
export const getChatMessages = async (chatId: string): Promise<ChatMsg[]> => {
  const data = await apiReq<BackendMessage[]>(`chat/${chatId}/messages`);
  if (!Array.isArray(data)) return [];
  return data.map((m) => ({
    role: (m.role?.toLowerCase() === 'user' ? 'User' : 'AI') as ChatMsg['role'],
    // Срезаем служебные хвосты (политики, контекст агента) — пользователь
    // должен видеть только свой исходный текст (EC-6)
    content: stripHiddenTails(m.content),
    created_at: m.created_at
      ? normalizeBackendUtcIsoString(m.created_at)
      : new Date().toISOString(),
  }));
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** role_id для chat API: UUID роли или slug (lawyer, hr, ops) */
export function resolveChatRoleId(roleId?: string, role?: string): string {
  const trimmedRoleId = roleId?.trim();
  if (trimmedRoleId && UUID_RE.test(trimmedRoleId)) return trimmedRoleId;
  const trimmedRole = role?.trim();
  if (trimmedRole) return trimmedRole;
  return 'lawyer';
}

/** Отправить сообщение (инициирует стриминг через WebSocket) */
export const sendMessage = async (
  chatId: string,
  body: SendMessageBody,
  roleId?: string
): Promise<SendMessageResponse | undefined> => {
  // Активные политики компании дописываются к каждому запросу (FR-5.4).
  // При выключенных политиках сообщение уходит без изменений (EC-5).
  const decorated: SendMessageBody = {
    ...body,
    human_message: applyCompanyPolicies(body.human_message),
  };
  return apiReq<SendMessageResponse>(
    `chat/${chatId}/messages/create?role_id=${encodeURIComponent(roleId ?? 'null')}`,
    'POST',
    decorated
  );
};

/** Очистить историю чата */
export const clearChat = async (chatId: string): Promise<{ deleted: number } | undefined> => {
  return apiReq<{ deleted: number }>(`chat/${chatId}/clear`, 'POST');
};

/** Остановить текущий стриминг */
export const stopChat = async (
  chatId: string,
  taskId: string
): Promise<{ result: string } | undefined> => {
  return apiReq<{ result: string }>(`chat/${chatId}/stop`, 'POST', { task_id: taskId });
};

/** Удалить чат */
export const deleteChat = async (chatId: string): Promise<void> => {
  await apiReq<void>(`chat/${chatId}/delete`, 'DELETE');
};

/** Переименовать чат */
export const renameChat = async (
  chatId: string,
  title: string
): Promise<ChatListing | undefined> => {
  return apiReq<ChatListing>(`chat/${chatId}/rename`, 'POST', { title });
};
